import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { logoutFromHelper } from "api/Helper";
import {
  GET_ALL_STAFF,
  STAFF_MEMBERS_CONTROLER_URL,
  STAFF_MEMBERS_CONTROLER_URL_ID,
  STAFF_MEMBERS_CONTROLER_URL_ID_DISABLE,
} from "api/Service";
import { SmsModal } from "components/Modal/SmsModal";
import { TabSwitcher } from "components/ui/TabSwitcher/TabSwitcher";
import { pathGenerators } from "utils/routes";

import { logout } from "../../../redux/auth/AuthSlice";
import { removeuser } from "../../../redux/user/userSlice";
import { StaffAvailabilitySection } from "./components/StaffAvailabilitySection";
import { UserFormModal } from "./components/UserFormModal";
import { UsersTable } from "./components/UsersTable";

const user_option = [];
const UserDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();

  const [user, setUser] = useState([]);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectAllChecked] = useState(false);
  const [selectedUsersForSMS, setSelectedUsersForSMS] = useState([]);

  const [filter, setFilter] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "user-availability") {
      return tabParam;
    }
    return "all-users";
  });

  const [rosteredStaff, setRosteredStaff] = useState([]);

  let tenantVal = useSelector((state) => state.auth.user?.tenant);
  if (!tenantVal) tenantVal = localStorage.getItem("kinesin-tenant");

  const [, setIsPageLoaded] = useState(false);

  // Memoised: passing a fresh object into the modal re-ran its reset effect on
  // every render of this page, wiping whatever was being typed or picked.
  const selectedStaff = useMemo(
    () => user.find((item) => item.id === selectedTaskId),
    [user, selectedTaskId]
  );

  const handleAddTask = () => {
    setSelectedTaskId(null);
    setIsUserFormOpen(true);
  };

  const createNewUser = async (userData) => {
    try {
      const axiosInstance = createAxiosInstance();
      let response;
      if (selectedTaskId) {
        const editedData = { ...userData, id: selectedTaskId };
        response = await axiosInstance.post(
          `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL}`,
          editedData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        response = await axiosInstance.post(
          `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL}`,
          userData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (selectedTaskId) {
        const updatedTasks = user.map((item) =>
          item.id === selectedTaskId ? { ...item, ...response.data } : item
        );
        setUser(updatedTasks);
        toast.success("User updated successfully");
      } else {
        setUser((prevService) => [...prevService, response.data]);
        toast.success("New User created successfully");
      }
      fetchUser();
      return response.data;
    } catch (error) {
      errorHandling(error);
      return null;
    }
  };

  /**
   * Enable or disable a staff member. The disable endpoint wants the whole
   * record, so the current one is fetched and posted back with `disabled`
   * flipped — one operation rather than staging the user in state first.
   *
   * The body is the fetched record spread whole, not a hand-listed subset. The subset it used to send
   * left out gmsNumber, deaspPanelNumber and qualification, and the backend's UPDATE writes every column
   * from the payload — so disabling and re-enabling a doctor silently erased their GMS contract number
   * (KIN-1935). Listing fields by hand means every field added later has to be remembered here too.
   */
  const toggleUserAccess = async (staffId) => {
    try {
      const axiosInstance = createAxiosInstance();
      const { data: current } = await axiosInstance.get(
        `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL_ID}${staffId}`
      );

      await axiosInstance.post(
        `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL_ID_DISABLE}`,
        {
          ...current,
          disabled: !current.disabled,
          tenant: tenantVal,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      toast.success(
        current.disabled ? "User enabled" : "User disabled"
      );
      fetchUser();
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    } catch (error) {
      errorHandling(error);
      toast.error("Failed to update user access");
    }
  };

  const handleEditTask = (id) => {
    setSelectedTaskId(id);
    setIsUserFormOpen(true);
  };

  const handleSMSModalClose = (userId) => {
    const updatedUser = user.map((item) =>
      item.id === userId ? { ...item, checked: false } : item
    );
    setUser(updatedUser);

    const selectedForSMS = updatedUser
      .filter((item) => item.checked)
      .map((item) => item.id);
    setSelectedUsersForSMS(selectedForSMS);
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantVal]);

  useEffect(() => {
    const filtered = user.filter((u) =>
      (u.firstName + " " + (u.lastName || ""))
        .toLowerCase()
        .includes(filter.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [filter, user]);

  useEffect(() => {
    if (tenantVal) {
      fetchRosteredStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantVal]);

  const fetchRosteredStaff = async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${tenantVal}${GET_ALL_STAFF}`);
      if (response && response.data) {
        setRosteredStaff(response.data);
      }
    } catch (error) {
      console.error("Error fetching rostered staff:", error);
      setRosteredStaff([]);
    }
  };

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}${STAFF_MEMBERS_CONTROLER_URL}`
      );
      if (response && response.data) {
        setUser(response.data);
        setIsPageLoaded(true);
      }
    } catch (error) {
      errorHandling(error);
    } finally {
      setIsLoading(false);
    }
  };

  const errorHandling = (error) => {
    console.error(error);
    toast.error(error.message);
    if (error && error.response && error.response.status) {
      if (error.response.status === 403) {
        logoutFromHelper();
        dispatch(logout());
        dispatch(removeuser());
        navigate(pathGenerators.auth.logout());
      }
    }
  };

  const handleCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied to clipboard");
    } catch (err) {
      toast.error("Failed to copy email");
    }
  };

  // A non-recurring availability is a single date: the start date. The end
  // date and the weekday flags do not apply, so they are neither editable nor
  // sent — the backend derives them from the start date (KIN-1671).

  const tabSwitcher = (
    <TabSwitcher
      options={[
        { value: "all-users", label: "All users" },
        { value: "user-availability", label: "User availability" },
      ]}
      value={activeTab}
      onChange={setActiveTab}
      fullWidth
    />
  );

  return (
    <>
      <div className="user_detailsbox">
        {activeTab === "all-users" && (
          <UsersTable
            tabs={tabSwitcher}
            users={filteredUsers}
            isLoading={isLoading}
            search={filter}
            onSearchChange={setFilter}
            onAddUser={handleAddTask}
            onEditUser={handleEditTask}
            onToggleAccess={toggleUserAccess}
            onCopyEmail={handleCopyEmail}
          />
        )}

        {activeTab === "user-availability" && (
          <StaffAvailabilitySection
            tabs={tabSwitcher}
            tenantVal={tenantVal}
            staffMembers={rosteredStaff}
          />
        )}
      </div>
      <SmsModal
        tenantVal={tenantVal}
        selectedUsers={selectedUsersForSMS}
        user_option={user_option}
        handleSMSModalClose={handleSMSModalClose}
        selectAllChecked={selectAllChecked}
      />
      <UserFormModal
        isOpen={isUserFormOpen}
        onClose={() => setIsUserFormOpen(false)}
        staffId={selectedTaskId}
        staff={selectedStaff}
        onSave={createNewUser}
      />
    </>
  );
};

export { UserDetailsPage };
